import type { Filter } from '../types/SortAndFilter';

export enum FilterGroupType {
  Individual = 'individual',
  Grouped = 'grouped',
}

export interface FilterGroup {
  type: FilterGroupType;
  key: string;
  name: string;
  filters: Filter[];
  selectedCount?: number;
}

const hasEmptyOptions = (filter: Filter): boolean => {
  if (!filter) {
    return true;
  }

  if ('options' in filter) {
    return !filter.options || filter.options.length === 0;
  }

  if ('max' in filter && 'min' in filter) {
    if (filter.max === filter.min) {
      return true;
    }
  }

  return false;
};

export const countSelectedValuesInFilter = (filter: Filter): number => {
  if (!filter) {
    return 0;
  }

  if ('options' in filter && Array.isArray(filter.options)) {
    return filter.options.reduce((count, option) => count + (option?.selected ? 1 : 0), 0);
  }

  let selectedCount = 0;

  if ('selectedMin' in filter && filter.selectedMin !== null) {
    selectedCount++;
  }
  if ('selectedMax' in filter && filter.selectedMax !== null) {
    selectedCount++;
  }

  return selectedCount;
};

const createIndividualFilter = (filter: Filter): FilterGroup => ({
  type: FilterGroupType.Individual,
  key: filter.key,
  name: filter.name,
  filters: [filter],
});

const createFilterGroup = (filter: Filter, groupFilters: Filter[]): FilterGroup => ({
  type: FilterGroupType.Grouped,
  key: filter.groupKey,
  name: filter.groupName,
  filters: groupFilters,
});

const countSelectedValuesInGroup = (filters: Filter[]): number => {
  if (!Array.isArray(filters) || filters.length === 0) {
    return 0;
  }

  return filters.reduce((total, filter) => total + countSelectedValuesInFilter(filter), 0);
};

export const createGroupDisplayFilter = (filterGroup: FilterGroup) => {
  const firstFilter = filterGroup.filters[0];

  const groupSelectedCount = countSelectedValuesInGroup(filterGroup.filters);

  return {
    ...firstFilter,
    key: filterGroup.key,
    name: filterGroup.name,
    groupCount: groupSelectedCount,
    options: [],
  };
};

export const groupFilters = (filters: Filter[]) => {
  const result: FilterGroup[] = [];
  const groups = new Map<string, Filter[]>();
  const processedGroups = new Set<string>();
  const processedFilterKeys = new Set<string>();

  for (const filter of filters) {
    if (hasEmptyOptions(filter)) {
      continue;
    }

    const groupKey = filter.groupKey;

    if (processedFilterKeys.has(filter.key)) {
      continue;
    }

    if (!groupKey) {
      result.push(createIndividualFilter(filter));
      processedFilterKeys.add(filter.key);
    } else {
      let groupArray = groups.get(groupKey);
      if (!groupArray) {
        groupArray = [];
        groups.set(groupKey, groupArray);
      }
      groupArray.push(filter);

      if (!processedGroups.has(groupKey)) {
        processedGroups.add(groupKey);
        result.push(createFilterGroup(filter, groupArray));

        for (const groupFilter of groupArray) {
          processedFilterKeys.add(groupFilter.key);
        }
      }
    }
  }

  return result;
};

const getOptionFilterText = (filter: Filter) => {
  if (!('options' in filter)) return null;

  const selectedOptions = filter.options
    .filter(option => option.selected)
    .map(option => option.value);

  return selectedOptions.length > 0 ? `${filter.name}: ${selectedOptions.join(' | ')}` : null;
};

const getRangeFilterText = (filter: Filter) => {
  if ('options' in filter) return null;

  const { selectedMin, selectedMax, min, max, unit } = filter;

  if (selectedMin === min && selectedMax === max) return null;

  const hasMin = selectedMin !== null;
  const hasMax = selectedMax !== null;

  if (hasMin && hasMax) {
    return `${filter.name}: ${selectedMin}${unit} - ${selectedMax}${unit}`;
  }
  if (hasMin) {
    return `${filter.name}: ab ${selectedMin}${unit}`;
  }
  if (hasMax) {
    return `${filter.name}: bis ${selectedMax}${unit}`;
  }

  return null;
};

export const getGroupAppliedValues = (filters: Filter[]) => {
  const appliedValues = filters
    .map(filter => getOptionFilterText(filter) || getRangeFilterText(filter))
    .filter(Boolean);

  return appliedValues.length > 0 ? appliedValues.join(' | ') : null;
};
